import { BlogPost } from '../types';

export const blogPosts: BlogPost[] = [
  {
    id: 1,
    title: '10 Ways to Make a Small Space Feel Larger',
    slug: '10-ways-to-make-small-space-feel-larger',
    excerpt: 'Discover practical design tricks to visually expand your compact living spaces without moving walls.',
    content: `
      # 10 Ways to Make a Small Space Feel Larger

      Living in a small space doesn't mean you have to feel cramped. With the right design strategies, you can create the illusion of a more spacious environment. Here are our top 10 tips:

      ## 1. Use Light Colors
      Light, neutral colors reflect more light and make walls appear to recede, creating an airy feeling. Consider whites, creams, and soft pastels for your walls.

      ## 2. Incorporate Mirrors
      Strategically placed mirrors reflect both natural and artificial light, making a room feel larger and brighter. Consider placing a large mirror opposite a window.

      ## 3. Choose Multi-functional Furniture
      Opt for furniture that serves multiple purposes, such as a sofa bed, an ottoman with storage, or a dining table that can also be used as a desk.

      ## 4. Keep it Minimal
      The more items you have in a space, the more cluttered and small it will feel. Be intentional about what you keep in your space.

      ## 5. Use Proper Lighting
      Good lighting can make a space feel larger. Layer your lighting with ambient, task, and accent lights to create depth.

      ## 6. Create Vertical Interest
      Draw the eye upward with tall bookshelves, vertical stripes, or artwork that extends toward the ceiling.

      ## 7. Use Clear Furniture
      Glass or acrylic tables and chairs take up less visual space, making the room feel more open.

      ## 8. Hang Curtains High
      Mount curtain rods close to the ceiling rather than just above the window to create the illusion of height.

      ## 9. Choose Low, Streamlined Furniture
      Furniture with exposed legs and lower profiles makes a room feel more spacious by allowing you to see more floor.

      ## 10. Maintain Consistency
      A cohesive color scheme and style throughout your space creates flow and makes it feel larger.

      By implementing these strategies, you can transform even the smallest spaces into comfortable, visually spacious environments. Need help applying these principles to your home? Book a consultation with our design experts today.
    `,
    image: 'https://images.pexels.com/photos/1669799/pexels-photo-1669799.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    author: 'Tammy Brooks',
    date: 'April 15, 2023',
    categories: ['Small Spaces', 'Interior Design', 'Tips & Tricks']
  },
  {
    id: 2,
    title: 'How to Choose the Perfect Color Palette for Your Home',
    slug: 'how-to-choose-perfect-color-palette',
    excerpt: 'Learn the fundamental principles of color theory and how to apply them to create a harmonious home interior.',
    content: `
      # How to Choose the Perfect Color Palette for Your Home

      Selecting the right colors for your home can be a daunting task. The colors you choose will set the mood and feel of your space, so it's important to get it right. Here's a guide to help you create a color palette that works for your home and reflects your personal style.

      ## Understand Color Theory Basics

      Before diving into specific colors, it helps to understand some color theory:

      - **Primary colors**: Red, blue, and yellow
      - **Secondary colors**: Purple, green, and orange (created by mixing primary colors)
      - **Tertiary colors**: Created by mixing primary and secondary colors
      - **Warm colors**: Reds, oranges, and yellows (energizing)
      - **Cool colors**: Blues, greens, and purples (calming)

      ## Consider the 60-30-10 Rule

      A classic interior design principle:
      - 60% dominant color (walls, large furniture)
      - 30% secondary color (accent furniture, textiles)
      - 10% accent color (accessories, artwork)

      ## Start With What You Have

      Consider existing elements that can't be easily changed:
      - Flooring
      - Cabinetry
      - Large furniture pieces
      - Architectural features

      ## Consider the Room's Purpose

      Different rooms serve different functions and should evoke appropriate feelings:
      - **Bedrooms**: Calming colors like blues, lavenders, and soft neutrals
      - **Home offices**: Focus-enhancing colors like greens or blues
      - **Kitchens**: Appetizing colors like warm neutrals or vibrant accents
      - **Living rooms**: Welcoming colors that reflect your personality

      ## Test Before Committing

      Always test paint colors in your actual space:
      - Get sample pots and paint swatches on your walls
      - View them at different times of day
      - See how they look under both natural and artificial light

      ## Create Flow Between Rooms

      Your home should feel cohesive:
      - Use a consistent neutral base throughout
      - Vary the intensity of a color palette from room to room
      - Consider how rooms connect visually

      Need professional guidance with your color selections? Our Color Palette Selection service provides expert advice tailored to your space and preferences. Book a consultation today to transform your home with the perfect colors.
    `,
    image: 'https://images.pexels.com/photos/1148955/pexels-photo-1148955.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    author: 'Lisa Martinez',
    date: 'March 3, 2023',
    categories: ['Color Theory', 'Interior Design', 'Home Decor']
  },
  {
    id: 3,
    title: 'Sustainable Design: Eco-Friendly Home Decor Ideas',
    slug: 'sustainable-design-eco-friendly-home-decor',
    excerpt: 'Discover how to create a beautiful home while minimizing environmental impact with these sustainable design strategies.',
    content: `
      # Sustainable Design: Eco-Friendly Home Decor Ideas

      Creating a beautiful home doesn't have to come at the expense of the environment. Sustainable interior design focuses on reducing environmental impact while creating healthy, beautiful living spaces. Here are some eco-friendly design ideas to consider for your next home update.

      ## Choose Sustainable Materials

      Opt for furniture and decor made from:
      - Reclaimed or recycled wood
      - Bamboo (fast-growing and renewable)
      - Cork (harvested without killing trees)
      - Organic cotton, linen, or hemp textiles
      - Water-based, low-VOC paints and finishes

      ## Invest in Quality, Timeless Pieces

      - Choose well-made furniture that will last for years
      - Select classic designs that won't quickly go out of style
      - Repair items rather than replacing them when possible

      ## Embrace Vintage and Second-hand

      - Shop antique stores, thrift shops, and online marketplaces
      - Give new life to existing pieces with refinishing or reupholstering
      - Mix vintage finds with contemporary items for an eclectic, personalized look

      ## Optimize Energy Efficiency

      - Use LED lighting throughout your home
      - Install smart thermostats and lighting systems
      - Choose energy-efficient appliances
      - Use thermal curtains to improve insulation

      ## Bring Nature Indoors

      - Incorporate houseplants to improve air quality
      - Use natural materials and textures
      - Maximize natural light to reduce electricity usage
      - Create indoor-outdoor connections where possible

      ## Reduce, Reuse, Repurpose

      - Get creative with repurposing items for new uses
      - Choose multi-functional furniture to reduce the number of items needed
      - Consider the full lifecycle of products before purchasing

      ## Work With Eco-Conscious Professionals

      When hiring designers or contractors, look for those who:
      - Specialize in sustainable design practices
      - Source eco-friendly materials
      - Minimize waste in their processes
      - Have relevant green certifications

      At Harmony Home Decor, we're committed to environmentally responsible design practices. Our consultants can help you create a beautiful, sustainable home that aligns with your values. Contact us to learn more about our eco-friendly design services.
    `,
    image: 'https://images.pexels.com/photos/2635038/pexels-photo-2635038.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    author: 'David Chen',
    date: 'May 22, 2023',
    categories: ['Sustainable Design', 'Eco-Friendly', 'Green Living']
  }
];